/**
 * @file Mode-invariant semantic colours for JS-only colour props (SE-8565).
 * @description A few places cannot take a class: a `Spinner color`, an icon
 * `stroke`, a colour handed to a gesture-handler action. They used to carry a
 * hex literal. This module gives them the semantic tier instead — but ONLY
 * for the tokens whose value is the same in Light and Dark, because a JS
 * constant cannot follow the theme the way a class does. A mode-varying token
 * (`content-primary`, `surface-base`, …) has no JS form here: use its class.
 *
 * This is not a new token. Each entry is an existing tier token, and its
 * value is DERIVED from the sources the classes compile from:
 *
 *  - a token that tailwind.config.js defines as a literal (`content-on-accent`)
 *    is read straight from tailwind.config.js;
 *  - a var-backed token is read from `PROVIDER_VALUES` below, which mirrors
 *    the gluestack provider config (src/components/ui/gluestack-ui-provider/
 *    config.ts) per mode. The provider's `vars()` is opaque on native, so it
 *    cannot be read at runtime; src/config/__tests__/semanticColors.test.ts
 *    pins every mirrored triplet to the provider in BOTH modes, and the
 *    tailwind entry to the variable it names.
 *
 * Load throws when a token's Light and Dark values differ, so a token that
 * design splits per mode cannot keep a JS constant by accident.
 *
 * `outline-card` and `status-financial` are mode-invariant too, but their
 * values are still provisional (docs/design-system/tokens.md), so they are
 * deliberately NOT exported (SE-8565 lead decision Q2).
 * @module config/semanticColors
 */
import tailwindConfig from "../../tailwind.config.js";

/** The mode-invariant tier tokens a JS colour prop may read. */
export type ModeInvariantColorToken =
  | "content-on-accent"
  | "accent-secondary"
  | "surface-action"
  | "accent-selection";

/**
 * Light / Dark provider values of the var-backed tokens (space-separated
 * `r g b`, as the provider writes them), with the CSS variable
 * tailwind.config.js points each token at. Mirrors the provider config; the
 * test fails the moment the two disagree.
 */
const PROVIDER_VALUES: Readonly<
  Record<
    Exclude<ModeInvariantColorToken, "content-on-accent">,
    {
      readonly variable: string;
      readonly light: string;
      readonly dark: string;
    }
  >
> = {
  "accent-secondary": {
    variable: "--color-accent-secondary",
    light: "11 141 205",
    dark: "11 141 205",
  },
  "surface-action": {
    variable: "--color-surface-action",
    light: "44 44 46",
    dark: "44 44 46",
  },
  "accent-selection": {
    variable: "--color-accent-selection",
    light: "74 222 128",
    dark: "74 222 128",
  },
};

const COLORS = (
  tailwindConfig as {
    theme: { extend: { colors: Record<string, Record<string, string>> } };
  }
).theme.extend.colors;

/**
 * Normalises a colour value to lowercase `#rrggbb`.
 * @param value - `#FFFFFF`, `#fff` or an `r g b` triplet.
 * @returns The hex, or null when the value is none of those.
 */
export function toHex(value: string): string | null {
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value.trim())?.[1];
  if (hex) {
    const six =
      hex.length === 3
        ? [...hex].map(channel => channel + channel).join("")
        : hex;
    return `#${six.toLowerCase()}`;
  }
  const channels = value.trim().split(/\s+/).map(Number);
  if (
    channels.length !== 3 ||
    channels.some(c => !Number.isInteger(c) || c < 0 || c > 255)
  ) {
    return null;
  }
  return `#${channels.map(c => c.toString(16).padStart(2, "0")).join("")}`;
}

/**
 * The single value of a token that must not vary by mode.
 * @param token - The token, for the error message.
 * @param light - Its Light value (hex or `r g b`).
 * @param dark - Its Dark value (hex or `r g b`).
 * @returns The shared value as lowercase `#rrggbb`.
 * @throws {TypeError} When either value is unreadable or the two differ — a
 * mode-varying token cannot be a JS constant; use its class.
 */
export function modeInvariantHex(
  token: string,
  light: string,
  dark: string
): string {
  const lightHex = toHex(light);
  const darkHex = toHex(dark);
  if (lightHex === null || darkHex === null) {
    throw new TypeError(
      `config/semanticColors: ${token} has an unreadable value (light "${light}", dark "${dark}") (SE-8565).`
    );
  }
  if (lightHex !== darkHex) {
    throw new TypeError(
      `config/semanticColors: ${token} is ${lightHex} in light and ${darkHex} in dark — a mode-varying token has no JS constant; use its class so it follows the theme (SE-8565).`
    );
  }
  return lightHex;
}

/**
 * The tailwind.config.js entry of a `family-key` token.
 * @param token - e.g. `content-on-accent`.
 * @returns The configured value.
 */
const configured = (token: ModeInvariantColorToken): string => {
  const dash = token.indexOf("-");
  const value = COLORS[token.slice(0, dash)]?.[token.slice(dash + 1)];
  if (value === undefined) {
    throw new TypeError(
      `config/semanticColors: tailwind.config.js has no ${token} colour — the tier and this module have drifted (SE-8565).`
    );
  }
  return value;
};

/**
 * Resolves one token: a literal from tailwind.config.js, or the mirrored
 * provider pair for a var-backed token (whose tailwind entry must still name
 * the mirrored variable).
 * @param token - The token.
 * @returns Its value as lowercase `#rrggbb`.
 */
const resolve = (token: ModeInvariantColorToken): string => {
  const value = configured(token);
  if (token === "content-on-accent") {
    return modeInvariantHex(token, value, value);
  }
  const provider = PROVIDER_VALUES[token];
  if (!value.includes(`var(${provider.variable})`)) {
    throw new TypeError(
      `config/semanticColors: tailwind.config.js ${token} no longer reads ${provider.variable} — update PROVIDER_VALUES from the provider config (SE-8565).`
    );
  }
  return modeInvariantHex(token, provider.light, provider.dark);
};

const TOKENS: readonly ModeInvariantColorToken[] = [
  "content-on-accent",
  "accent-secondary",
  "surface-action",
  "accent-selection",
];

/**
 * Mode-invariant semantic colours as `#rrggbb`, for props that cannot take a
 * class (SE-8565). Prefer the class wherever one works.
 */
export const SEMANTIC_COLORS: Readonly<
  Record<ModeInvariantColorToken, string>
> = Object.freeze(
  Object.fromEntries(TOKENS.map(token => [token, resolve(token)])) as Record<
    ModeInvariantColorToken,
    string
  >
);
